import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Copy, Eye, Mail, Plus, Trash2, UserPlus } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { ClientPerson, ClientPersonCard } from '../../types/aai';
import { Action, Chip, Field, Input, Loading, Select } from '../../components/aa';
import { OrbitDeck } from '../../components/orbit-deck/OrbitDeck';
import type { DeckAnswer, DeckCardModel } from '../../components/orbit-deck/types';
import { transcribeFor } from '../orbit-deck/deckModel';

/*
 * The Person intake step of a client's journey.
 *
 * Not the team roster: this is the intake each person answers about their own
 * work, the same cards somebody scanning the partner's code gets. The partner
 * adds the people who should answer it, and for each one can:
 *
 *   send their link     it opens their own session, no name to type and no
 *                       employer to give, and carries on where it stopped
 *   fill in for them    any card; the person sees it first, filled in, to keep
 *                       or change the next time they open their link
 *   watch it            how far they are, which block they are on, which
 *                       answers are theirs and which were filled for them
 *
 * Everyone who finishes it is added to the client's team with the role set
 * here. Several people can be added, so a partner is never waiting on one.
 */

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner',
  AI_CHAMPION: 'AI Champion',
  DEPARTMENT_HEAD: 'Department head',
  AI_PARTICIPANT: 'Employee',
  TRAINER: 'Trainer',
  EXPERT: 'Expert',
};

const errorText = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { detail?: string } } };
  return e.response?.data?.detail || fallback;
};

const when = (iso: string): string => {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const INPUT_OF: Record<string, DeckCardModel['input']> = {
  select: 'choose',
  multiselect: 'choose_many',
  number: 'number',
  text: 'text',
  longtext: 'say',
};

const heldLine = (card: ClientPersonCard): string => {
  const who = card.filled_by || 'you';
  if (card.by === 'partner' && card.to_confirm) return `Filled by ${who}, for them to confirm`;
  if (card.by === 'partner') return `Filled by ${who}, confirmed`;
  return 'Answered by them';
};

/* Every card they are dealt, in one deck, the same deck the rest of the Room
   uses: an answered card comes with its answer already switched on and says
   who gave it, an empty one waits for an answer. Saving changes the answer;
   leaving it as it is moves on. */
const PersonDeck: React.FC<{
  clientId: string;
  person: ClientPerson;
  onChanged: (next: ClientPerson) => void;
}> = ({ clientId, person, onChanged }) => {
  const [round, setRound] = useState(0);
  const [through, setThrough] = useState(false);
  const transcribe = useMemo(() => transcribeFor(clientId), [clientId]);

  /* Dealt once per round, so saving an answer does not reshuffle the deck
     under the partner's hands. */
  const cards = useMemo<DeckCardModel[]>(() => person.blocks.flatMap((b) => b.cards
    .filter((c) => !c.skipped || c.answered)
    .map((card) => {
      const picked = Array.isArray(card.value) ? (card.value as string[]) : [String(card.value ?? '')];
      return {
        id: `${person.id}:${round}:${card.key}`,
        tag: card.answered ? 'confirm' : 'ask',
        tagLabel: card.answered ? `${heldLine(card)} · ${b.title}` : b.title,
        title: card.label,
        why: card.why,
        input: INPUT_OF[card.kind] || 'say',
        options: card.options.map((o) => ({
          value: o.value,
          label: o.subtitle ? `${o.label} (${o.subtitle})` : o.label,
          detected: card.answered && picked.includes(o.value),
        })),
        placeholder: card.placeholder,
        prefill: card.answered && !card.options.length ? String(card.value ?? '') : undefined,
        detectedNote: 'Their answer is switched on. Tap to change it.',
        fieldKey: card.key,
      } as DeckCardModel;
    })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [person.id, round]);

  const answer = async (card: DeckCardModel, value: DeckAnswer) => {
    try {
      const r = await aaiApi.fillForPerson(clientId, person.id, card.fieldKey || '', value.value);
      onChanged(r.data);
    } catch (err) {
      toast.error(errorText(err, 'Could not save that answer.'));
      throw new Error('answer failed');
    }
  };

  const document = async () => {
    toast.info('A document is read into the company, from the Business intake. Here, answer or leave each card.');
    return undefined;
  };

  if (through || cards.length === 0) {
    return (
      <div className="rounded-[14px] border border-line bg-[var(--aa-card)] px-5 py-4 text-[13px] text-ink">
        {['answered', 'invited', 'done'].includes(person.stage)
          ? `That is every card. Everything ${person.name} is asked is answered.`
          : `That is every card. Whatever is still empty is ${person.name}'s to answer from their link.`}
        <button type="button" className="ml-2 font-semibold underline" onClick={() => { setRound((n) => n + 1); setThrough(false); }}>
          Go through them again
        </button>
      </div>
    );
  }

  return (
    <OrbitDeck
      key={`${person.id}:${round}`}
      cards={cards}
      onAnswer={answer}
      onSkip={() => undefined}
      onDocument={document}
      onDone={() => setThrough(true)}
      transcribe={transcribe}
      labels={{
        deckTitle: 'Their cards',
        deckNote: `${person.answered} of ${person.total} answered`,
        primary: 'Save for them',
        skip: 'Leave it as it is',
        skipWord: 'next',
      }}
    />
  );
};

const initialsOf = (name: string): string => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

/* What a partner can do to one person, the same from the list and their page. */
const usePersonActions = (clientId: string, person: ClientPerson, onChanged: (next: ClientPerson | null) => void) => {
  const [busy, setBusy] = useState('');
  const act = async (what: string, work: () => Promise<void>) => {
    setBusy(what);
    try {
      await work();
    } finally {
      setBusy('');
    }
  };
  const invite = () => act('invite', async () => {
    try {
      const r = await aaiApi.invitePerson(clientId, person.id);
      toast.success(`Invite sent to ${r.data.email}.`);
      onChanged({ ...person, invited_at: new Date().toISOString() });
    } catch (err) {
      toast.error(errorText(err, 'Could not send the invite.'));
    }
  });
  const copy = () => act('copy', async () => {
    try {
      const r = await aaiApi.personIntakeLink(clientId, person.id);
      await navigator.clipboard.writeText(r.data.url);
      toast.success(`${person.name}'s link is copied.`);
    } catch (err) {
      toast.error(errorText(err, 'Could not copy the link.'));
    }
  });
  const role = (next: string) => act('role', async () => {
    try {
      const r = await aaiApi.setPersonRole(clientId, person.id, next);
      onChanged(r.data);
    } catch (err) {
      toast.error(errorText(err, 'Could not change the role.'));
    }
  });
  const remove = () => {
    if (!window.confirm(`Remove ${person.name}? Their answers so far go with them.`)) return;
    act('remove', async () => {
      try {
        await aaiApi.removeClientPerson(clientId, person.id);
        onChanged(null);
      } catch (err) {
        toast.error(errorText(err, 'Could not remove them.'));
      }
    });
  };
  const login = () => act('login', async () => {
    try {
      const r = await aaiApi.joinClientPerson(clientId, person.id);
      onChanged(r.data);
      if (r.data.member_at) toast.success(`Login invite sent to ${person.email}.`);
      else toast.warning(r.data.member_note || 'They could not be added to the team.');
    } catch (err) {
      toast.error(errorText(err, 'Could not send the login invite.'));
    }
  });
  return { busy, invite, copy, role, remove, login };
};

/* Where they are, in the order it happens: answering, all answered, invited
   to sign in, signed in. */
const StageChip: React.FC<{ person: ClientPerson }> = ({ person }) => {
  if (person.stage === 'done') return <Chip tone="ok">Done</Chip>;
  if (person.stage === 'invited') return <Chip tone="run">Invited to sign in</Chip>;
  if (person.stage === 'answered' && person.member_note) return <Chip tone="warn">Answered, not in the team</Chip>;
  if (person.stage === 'answered') return <Chip tone="warn">Answered, login not sent</Chip>;
  if (person.stage === 'under_way') return <Chip tone="neutral">Answering</Chip>;
  if (person.invited_at) return <Chip tone="neutral">Cards sent {when(person.invited_at)}</Chip>;
  return <Chip tone="neutral">Not started</Chip>;
};

const answering = (p: ClientPerson): boolean => p.stage === 'not_started' || p.stage === 'under_way';

/* One person in the list: who they are, how far they are, and the way in. */
const PersonRow: React.FC<{
  clientId: string;
  person: ClientPerson;
  onOpen: () => void;
  onChanged: (next: ClientPerson | null) => void;
}> = ({ clientId, person, onOpen, onChanged }) => {
  const { busy, invite, copy, remove, login } = usePersonActions(clientId, person, onChanged);
  const asking = answering(person);
  const pct = person.total ? Math.round((person.answered / person.total) * 100) : 0;
  const iconBtn = 'grid h-8 w-8 place-items-center rounded-md border border-line bg-white text-ink-muted transition-colors hover:border-ink hover:text-ink disabled:opacity-40';
  return (
    <div className="grid grid-cols-[40px_minmax(0,1fr)] items-center gap-x-4 gap-y-3 border-b border-line-soft px-5 py-4 last:border-b-0 md:grid-cols-[40px_minmax(0,1fr)_200px_280px]">
      <span className="grid h-10 w-10 place-items-center rounded-full bg-[var(--aa-card)] text-[13px] font-bold text-ink">
        {initialsOf(person.name)}
      </span>
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[14px] font-semibold text-ink">{person.name}</span>
          <StageChip person={person} />
        </div>
        <div className="mt-0.5 truncate text-[12.5px] text-ink-dim">
          {[person.job_title, ROLE_LABELS[person.role] || person.role, person.email].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div className="col-span-2 md:col-span-1">
        <div className="mb-1.5 flex justify-between text-[11.5px] text-ink-dim">
          <span>{person.answered} of {person.total} answered</span>
          <span className="tabular-nums">{pct}%</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-canvas-inset">
          <div className="h-full rounded-full bg-ink" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="col-span-2 flex items-center justify-end gap-1.5 md:col-span-1">
        {person.stage === 'answered' && (
          <Action size="sm" disabled={Boolean(busy)} reason="Sending" onClick={login}>Send login invite</Action>
        )}
        {person.stage !== 'answered' && (
          <Action size="sm" variant={asking ? undefined : 'outline'} onClick={onOpen}>
            {asking ? 'Open their cards' : 'See their answers'}
          </Action>
        )}
        {asking && (
          <>
            <button type="button" aria-label={`Email ${person.name} their cards`} title={person.invited_at ? 'Email their cards again' : 'Email their cards'}
              onClick={invite} disabled={Boolean(busy)} className={iconBtn}>
              <Mail size={14} />
            </button>
            <button type="button" aria-label={`Copy ${person.name}'s link`} title="Copy their cards link"
              onClick={copy} disabled={Boolean(busy)} className={iconBtn}>
              <Copy size={14} />
            </button>
          </>
        )}
        {person.stage === 'answered' && (
          <button type="button" aria-label={`See ${person.name}'s answers`} title="See their answers" onClick={onOpen} className={iconBtn}>
            <Eye size={14} />
          </button>
        )}
        {person.stage === 'invited' && (
          <button type="button" aria-label={`Send ${person.name} their login invite again`} title="Send the login invite again"
            onClick={login} disabled={Boolean(busy)} className={iconBtn}>
            <Mail size={14} />
          </button>
        )}
        <button type="button" aria-label={`Remove ${person.name}`} title="Remove" onClick={remove} disabled={Boolean(busy)}
          className="grid h-8 w-8 place-items-center rounded-md border border-line bg-white text-ink-muted transition-colors hover:border-danger hover:text-danger disabled:opacity-40">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

/* One person's own page: their deck in the middle, who they are and how far
   they have got beside it, and the way back to everyone else. */
const PersonPage: React.FC<{
  clientId: string;
  person: ClientPerson;
  roles: string[];
  backLabel: string;
  onBack: () => void;
  onChanged: (next: ClientPerson | null) => void;
}> = ({ clientId, person, roles, backLabel, onBack, onChanged }) => {
  const { busy, invite, copy, role, remove, login } = usePersonActions(clientId, person, (next) => {
    onChanged(next);
    if (!next) onBack();
  });
  const asking = answering(person);
  const done = !asking;
  return (
    <>
      <div className="det-head">
        <button type="button" className="back" onClick={onBack}>← {backLabel}</button>
        <h1>{person.name}</h1>
        <span className="pct"><b>{person.answered}</b> of {person.total} answered</span>
      </div>
      <div className="fill-wrap wide">
        {done && (
          <p className="fill-purpose">
            Every card {person.name} answered, with their answer already in it and who gave it. <b>Change</b> any of them, or leave it as it is.
          </p>
        )}
        {!done && (
          <p className="fill-purpose">
            The cards {person.name} is dealt. <b>Answer what you already know</b>, and leave the rest for them: whatever you fill in, they see
            first on their link, to keep or change.
          </p>
        )}
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1">
            <PersonDeck clientId={clientId} person={person} onChanged={onChanged} />
          </div>
          <div className="fill-aside w-full shrink-0 lg:w-[360px]">
            <div className="rounded-[14px] border border-line bg-white p-4">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--aa-card)] text-[13px] font-bold text-ink">
                  {initialsOf(person.name)}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[14px] font-semibold text-ink">{person.name}</div>
                  <div className="truncate text-[12px] text-ink-dim">{person.job_title || person.email}</div>
                </div>
              </div>
              <div className="mt-3 text-[12px] text-ink-dim">{person.email}</div>
              <label className="mt-3 block text-[12px] font-semibold text-ink">
                Role once they have answered
                <Select
                  aria-label={`${person.name}'s role`}
                  value={person.role}
                  disabled={Boolean(busy)}
                  onChange={(e) => role(e.target.value)}
                  className="mt-1.5 h-9 py-0 text-[13px] font-normal"
                >
                  {roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                </Select>
              </label>
              {asking && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Action size="sm" icon={<Mail size={13} />} disabled={Boolean(busy)} reason="Sending" onClick={invite}>
                    {person.invited_at ? 'Email their cards again' : 'Email their cards'}
                  </Action>
                  <Action size="sm" variant="outline" icon={<Copy size={13} />} disabled={Boolean(busy)} reason="Copying" onClick={copy}>
                    Copy link
                  </Action>
                </div>
              )}
              <div className="mt-3 text-[12px] leading-[18px] text-ink-dim">
                {asking && person.invited_at && `Cards sent ${when(person.invited_at)}. Their link picks up where they left off.`}
                {asking && !person.invited_at && 'Their cards are not sent yet. Email them, or fill them in here.'}
                {person.stage === 'invited' && `In the team as ${ROLE_LABELS[person.role] || person.role}. Their login invite is sent; waiting for them to sign in.`}
                {person.stage === 'done' && `Signed in. In the team as ${ROLE_LABELS[person.role] || person.role}.`}
              </div>
            </div>
            {person.stage === 'answered' && (
              <div className="rounded-[14px] border border-[var(--aa-yellow,#e8d27a)] bg-[var(--aa-yellow-soft,#fbf6dc)] p-4">
                <div className="text-[13px] font-semibold text-ink">Every card is answered</div>
                <p className="m-0 mt-1 text-[12.5px] leading-[19px] text-ink">
                  {person.member_note || `Send ${person.name} their login invite, so they can sign in and join the team.`}
                </p>
                <div className="mt-3">
                  <Action size="sm" icon={<Mail size={13} />} disabled={Boolean(busy)} reason="Sending" onClick={login}>Send login invite</Action>
                </div>
              </div>
            )}
            {person.stage === 'invited' && (
              <button type="button" onClick={login} disabled={Boolean(busy)}
                className="self-start text-[12px] font-semibold text-ink-muted hover:text-ink disabled:opacity-40">
                Send the login invite again
              </button>
            )}
            <div className="rounded-[14px] border border-line bg-white p-4">
              <div className="mb-2.5 text-[13px] font-semibold text-ink">How far they are</div>
              <ul className="m-0 list-none space-y-2 p-0">
                {person.blocks.map((b) => {
                  const n = b.cards.filter((c) => c.answered).length;
                  return (
                    <li key={b.section} className="flex items-center gap-2 text-[12.5px]">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${n === b.cards.length ? 'bg-ok-dim' : n > 0 ? 'bg-ink' : 'bg-line-strong'}`} />
                      <span className="min-w-0 flex-1 truncate text-ink">{b.title}</span>
                      <span className="tabular-nums text-ink-dim">{n}/{b.cards.length}</span>
                    </li>
                  );
                })}
              </ul>
              {person.to_confirm > 0 && (
                <p className="m-0 mt-3 text-[12px] text-ink-dim">{person.to_confirm} filled by you, for them to confirm.</p>
              )}
            </div>
            <button type="button" onClick={remove} disabled={Boolean(busy)}
              className="self-start text-[12px] font-semibold text-ink-muted hover:text-danger disabled:opacity-40">
              Remove {person.name}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const AddPerson: React.FC<{
  clientId: string;
  roles: string[];
  onAdded: (person: ClientPerson) => void;
  onCancel: () => void;
}> = ({ clientId, roles, onAdded, onCancel }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [role, setRole] = useState('AI_PARTICIPANT');
  const [busy, setBusy] = useState(false);
  const valid = name.trim().length >= 2 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  const add = async () => {
    setBusy(true);
    try {
      const r = await aaiApi.addClientPerson(clientId, { name: name.trim(), email: email.trim(), job_title: title.trim(), role });
      toast.success(`${r.data.name} added. Send their invite, or fill in their cards.`);
      onAdded(r.data);
    } catch (err) {
      toast.error(errorText(err, 'Could not add that person.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 rounded-[13px] border border-line bg-white p-5">
      <div className="mb-3 text-[15px] font-bold text-ink">Add a person</div>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Name"><Input value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="Priya Nair" /></Field>
        <Field label="Email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="priya@company.com" /></Field>
        <Field label="Job title (optional)"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Head of Operations" /></Field>
        <Field label="Role once they have answered" hint="You can change it any time.">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            {roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
          </Select>
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Action icon={<UserPlus size={14} />} disabled={!valid || busy} reason={busy ? 'Adding' : 'Give a name and an email'} onClick={add}>
          Add person
        </Action>
        <Action variant="quiet" onClick={onCancel}>Cancel</Action>
      </div>
    </div>
  );
};

export const PhasePersonIntake: React.FC<{
  clientId: string;
  title: string;
  onBack: () => void;
  onChanged: () => void;
}> = ({ clientId, title, onBack, onChanged }) => {
  const [people, setPeople] = useState<ClientPerson[] | null>(null);
  const [roles, setRoles] = useState<string[]>(Object.keys(ROLE_LABELS));
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await aaiApi.clientPeople(clientId);
      setPeople(r.data.people);
      if (r.data.roles?.length) setRoles(r.data.roles);
      setError('');
    } catch (err) {
      setError(errorText(err, 'Could not load the people for this intake.'));
      setPeople([]);
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const changed = (id: string, next: ClientPerson | null) => {
    setPeople((current) => {
      const list = current || [];
      if (!next) return list.filter((p) => p.id !== id);
      return list.map((p) => (p.id === id ? next : p));
    });
    onChanged();
  };

  const done = (people || []).filter((p) => p.stage === 'done').length;
  const opened = (people || []).find((p) => p.id === openId) || null;

  if (opened) {
    return (
      <PersonPage
        clientId={clientId}
        person={opened}
        roles={roles}
        backLabel={title}
        onBack={() => setOpenId('')}
        onChanged={(next) => changed(opened.id, next)}
      />
    );
  }

  return (
    <>
      <div className="det-head">
        <button type="button" className="back" onClick={onBack}>← Company</button>
        <h1>{title}</h1>
        {people && people.length > 0 && <span className="pct"><b>{done}</b> of {people.length} people done</span>}
      </div>
      <div className="dept-intro">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            A few questions each person answers about their <b>own work</b>: what they do, what drains their week, which tools they use
            and how far into AI they are. Send each their own link, or open their cards and fill in what you already know; they see
            your answers first and confirm them. <b>Everyone who finishes joins the team with the role you set.</b>
            <span className="mt-1.5 block"><b>Invite only.</b> Only the people added here, by you or the client&rsquo;s Owner, are asked it. Nobody else at the client sees
            this intake, and what each person answers shows here.</span>
          </div>
          {!adding && (
            <Action size="sm" icon={<Plus size={14} />} onClick={() => setAdding(true)}>Add a person</Action>
          )}
        </div>
      </div>
      <div>
        {adding && (
          <AddPerson
            clientId={clientId}
            roles={roles}
            onCancel={() => setAdding(false)}
            onAdded={(person) => {
              setAdding(false);
              setPeople((current) => [...(current || []), person]);
              onChanged();
            }}
          />
        )}

        {error && <div className="mb-4 text-[13px] text-danger">{error}</div>}
        {!people && <Loading label="Loading the people" />}
        {people && people.length === 0 && !adding && (
          <div className="rounded-[13px] border border-dashed border-line px-5 py-8 text-center text-[13px] text-ink-dim">
            Nobody added yet. Add the first person above.
          </div>
        )}
        {people && people.length > 0 && (
          <div className="overflow-hidden rounded-[13px] border border-line bg-white">
            {people.map((person) => (
              <PersonRow key={person.id} clientId={clientId} person={person}
                onOpen={() => setOpenId(person.id)}
                onChanged={(next) => changed(person.id, next)} />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default PhasePersonIntake;
